import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { ArrowRight, Check, Plus } from "lucide-react";
import { getConcept, DRAFT_PREVIEW, approvedPrice, isRetiredConcept } from "@/lib/concepts";
import { useInquiry } from "@/lib/inquiry-store";
import { toast } from "sonner";
import { faqJsonLd, serviceJsonLd } from "@/lib/seo.schemas";
export const Route = createFileRoute("/koncept/$slug")({
  loader: ({ params }) => {
    const c = getConcept(params.slug);
    if (!c) throw notFound();
    return c;
  },
  head: ({ loaderData: c }) =>
    c && isRetiredConcept(c.slug)
      ? {
          meta: [
            { title: `${c.name} – arkiv | Event Sverige` },
            {
              name: "description",
              content:
                "Parkens Aktivitetsdag är ett tidigare koncept som inte längre erbjuds. Upptäck våra aktuella koncept för gemensamma event.",
            },
          ],
          links: [{ rel: "canonical", href: `https://eventsverige.se/koncept/${c.slug}` }],
          scripts: [],
        }
      : c
        ? {
            meta: [
              {
                title: `${c.name} – ${c.segment === "massa" ? "mässaktivering" : "företagsevent"} | Event Sverige`,
              },
              { name: "description", content: c.intro },
              { property: "og:title", content: `${c.name} | Event Sverige` },
              { property: "og:description", content: c.tagline },
              ...(c.image ? [{ property: "og:image", content: c.image }] : []),
              ...(DRAFT_PREVIEW && c.contentStatus !== "approved"
                ? [{ name: "robots", content: "noindex,follow" }]
                : []),
            ],
            links: [{ rel: "canonical", href: `https://eventsverige.se/koncept/${c.slug}` }],
            scripts: [
              {
                type: "application/ld+json",
                children: JSON.stringify(serviceJsonLd(c.name, c.intro, `https://eventsverige.se/koncept/${c.slug}`)),
              },
              { type: "application/ld+json", children: JSON.stringify(faqJsonLd(c.faq)) },
            ],
          }
        : {},
  component: ConceptPage,
});
function ConceptPage() {
  const c = Route.useLoaderData();
  if (isRetiredConcept(c.slug)) {
    return (
      <div className="concept-site">
        <section className="experience-wrap experience-section">
          <p className="eyebrow">Tidigare koncept / arkiv</p>
          <h1>{c.name}</h1>
          <p className="page-intro">Parkens Aktivitetsdag är ett tidigare koncept som inte längre erbjuds.</p>
          <p>
            Sidan finns kvar för dig som har en äldre länk. Vi hjälper dig hitta ett aktuellt upplägg för gruppen och
            platsen.
          </p>
          <div className="hero-actions">
            <Link to="/koncept/$slug" params={{ slug: "speltorget" }} className="experience-button">
              Upptäck Speltorget <ArrowRight size={18} />
            </Link>
            <Link to="/koncept" className="experience-button outline">
              Alla aktuella koncept <ArrowRight size={18} />
            </Link>
          </div>
        </section>
      </div>
    );
  }
  return <ActiveConceptPage />;
}
function ActiveConceptPage() {
  const c = Route.useLoaderData();
  const { ensure, has } = useInquiry();
  const selected = has(`concept:${c.slug}`);
  const selectCurrent = () => ensure({ slug: `concept:${c.slug}`, name: c.name, image: c.image });
  return (
    <div className="concept-site">
      <section className="experience-hero">
        <div className="experience-wrap hero-grid">
          <div className="hero-copy">
            <p className="eyebrow">{c.segment === "massa" ? "Mässor & aktivering" : "Företagsevent"}</p>
            <h1>{c.name}</h1>
            <h2 className="concept-tagline">{c.tagline}</h2>
            <p className="hero-intro">{c.intro}</p>
            <div className="hero-actions">
              <button
                className="experience-button"
                disabled={selected}
                onClick={() => {
                  selectCurrent();
                  toast.success("Konceptet är tillagt i din förfrågan");
                }}
              >
                {selected ? <Check size={18} /> : <Plus size={18} />}{" "}
                {selected ? "Tillagt i förfrågan" : "Välj det här konceptet"}
              </button>
              <Link to="/forfragan" onClick={selectCurrent} className="experience-button outline">
                Till förfrågan <ArrowRight size={18} />
              </Link>
            </div>
            <div className="hero-notes">
              <span>{c.guests}</span>
              <span>{c.durationLabel}</span>
              <span>Ljus ingår</span>
            </div>
          </div>
          <div className="hero-photo">
            <div className="warm-glow" />
            {c.image ? (
              <img src={c.image} alt={c.imageAlt} width="900" height="1000" fetchPriority="high" />
            ) : c.slug === "mario-kart-cup" ? (
              <div className="mario-race-graphic mario-race-graphic-large" role="img" aria-label={c.imageAlt}>
                <span className="mario-controller" aria-hidden>
                  🏎️
                </span>
                <strong>Mario Kart Cup</strong>
                <small>Fysisk bilbana · två spelare per heat</small>
              </div>
            ) : (
              <div className="concept-image-fallback">Event Sverige</div>
            )}
            <div className="photo-note">
              <span>
                {c.slug === "vr-showcase" || c.slug === "vr-laguppdrag"
                  ? "Spelbild: Beat Saber"
                  : c.slug === "mario-kart-cup"
                    ? "Grafisk presentation av fysisk bilbana"
                    : "Utrustning från vårt sortiment"}
              </span>
              <strong>{c.imageAlt}</strong>
            </div>
          </div>
        </div>
      </section>
      <section className="experience-wrap experience-section detail-grid">
        <div>
          <p className="eyebrow">Tänk er det här</p>
          <h2>Så känns upplevelsen.</h2>
          <p className="page-intro">{c.story}</p>
          {c.flow.length > 1 && (
            <div className="delivery-steps compact">
              {c.flow.map((s, i) => (
                <div key={s}>
                  <span>0{i + 1}</span>
                  <h3>{s}</h3>
                </div>
              ))}
            </div>
          )}
          <h2 className="mt-10">
            {c.slug === "parkens-aktivitetsdag" ? "Planerat för platsen och vädret." : "På dagen eller kvällen."}
          </h2>
          {c.slug === "parkens-aktivitetsdag" ? (
            <p>
              Underlag, prognos, tillträde och reservyta styr det slutliga upplägget. Vi bekräftar vilka stationer som
              kan användas och hur eventet anpassas till platsen i offerten.
            </p>
          ) : (
            <p>
              Inomhus från september till april skapar vi en varm, välkomnande miljö. I juni passar samma koncept inför
              semestern. Stämningsljus ingår alltid; ljudnivån följer det valda upplägget och lämnar utrymme för samtal.
            </p>
          )}
        </div>
        <aside className="concept-panel">
          <p className="eyebrow">Tydligt före bokning</p>
          <h2>{c.packagePending ? "Upplägg efter kontroll" : "I grundupplägget"}</h2>
          <p className="quote-total">
            {approvedPrice(c) == null
              ? c.packagePending
                ? "Pris fastställs efter kontroll"
                : "Pris enligt offert"
              : `${approvedPrice(c)?.toLocaleString("sv-SE")} kr exkl. moms · transport tillkommer`}
          </p>
          <ul>
            {c.includes.map((x) => (
              <li key={x}>
                <Check size={16} />
                {x}
              </li>
            ))}
          </ul>
          <h3>Deltagande och turordning</h3>
          <p>{c.capacitySummary}</p>
          <h3>Planeringsyta</h3>
          <p>{c.approxArea}</p>
          <h3>Det här ordnar ni</h3>
          <ul>
            {c.customerProvides.map((x) => (
              <li key={x}>{x}</li>
            ))}
          </ul>
          <h3>Ingår inte</h3>
          <p>{c.notIncluded}</p>
          <h3>Tillval efter önskemål</h3>
          <p>{c.optionalExtras}</p>
          <h3>Rigg och leverans</h3>
          <p>{c.setupSummary}</p>
          {!c.packagePending && (
            <p>
              Ordinarie personal, planering, rigg, nedtagning och stämningsljus ingår i paketpriset. Extra arbete
              debiteras bara utanför det beskrivna upplägget efter godkännande i förväg.
            </p>
          )}
          <Link to="/forfragan" onClick={selectCurrent} className="experience-button">
            Be om offert <ArrowRight size={18} />
          </Link>
        </aside>
      </section>
      {c.productSlugs.length > 0 && (
        <section className="experience-wrap">
          <h2>Spelen som ingår i upplevelsen</h2>
          <div className="equipment-links">
            {c.productSlugs.map((slug) => (
              <Link key={slug} to="/produkter/$slug" params={{ slug }}>
                {slug.replaceAll("-", " ")} <ArrowRight size={16} />
              </Link>
            ))}
          </div>
        </section>
      )}
      <section className="experience-wrap experience-section">
        <h2>Vanliga frågor</h2>
        {c.faq.map((f) => (
          <details key={f.q} className="concept-faq">
            <summary>{f.q}</summary>
            <p>{f.a}</p>
          </details>
        ))}
      </section>
      <section className="experience-wrap">
        <div className="planning-panel">
          <div>
            <p className="eyebrow">Nästa steg</p>
            <h2>Gör det till ert event.</h2>
            <p>
              Välj konceptet och berätta om datum, plats, deltagare och önskemål. Tillgänglighet och omfattning
              bekräftas i offerten.
            </p>
          </div>
          <Link to="/forfragan" onClick={selectCurrent} className="experience-button">
            Planera med oss <ArrowRight size={18} />
          </Link>
        </div>
      </section>
    </div>
  );
}
